import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import cn from "classnames";

import TableComponent from "components/ui-kit/react-table/TableComponent";
import { supportTableUtils } from "components/ui-kit/react-table/supportTableUtils";
// import SupportStateController from "components/ui-kit/state-controller/SupportStateController";
import TradersHelpers from "./helpers";
import { NewRequest } from "components/requests/NewRequest";
import { constants } from "utilities/constants";
import { tradeGroupThunks } from "action-thunks/tradeGroup-thunks";

import styles from "./styles.module.scss";

const { tableFormattedData, findActiveTP } = TradersHelpers;
const { categoryDataType } = constants;

const Traders = ({ adminGroupData }) => {
  const { TGAccountsList, riskSettingMetadata } = useSelector(
    (state) => state.tradeGroup
  );
  const { tradingPlatforms } = useSelector((state) => state.marketdata);

  const [checkedItems, setCheckedItems] = useState([]);
  // Pagination disabled: show full activeList (was visibleItems + SupportStateController)
  // const [visibleItems, setVisibleItems] = useState(0);
  const [sortedTraders, setSortedTraders] = useState(null);
  const [activeTP, setActiveTP] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoadingList, setIsLoadingisLoadingList] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  // const [filteredMeta, setFilteredMeta] = useState({
  //   filteredCount: 0,
  //   totalCount: 0,
  //   isFiltering: false,
  // });

  const activeList = useMemo(() => {
    if (sortedTraders && activeTP) {
      const joinedActiveTPName = `${activeTP?.software} ${activeTP?.softwareType}`;
      return sortedTraders[joinedActiveTPName];
    } else {
      return [];
    }
  }, [sortedTraders, activeTP]);

  const dispatch = useDispatch();

  useEffect(() => {
    const fetchRequestsPeriodically = () => {
      if (!isModalOpen) {
        if (isInitialLoad) setIsLoadingisLoadingList(true);
        dispatch(
          tradeGroupThunks.getTGAccountsListTC([
            "openMDrequest",
            "marketData",
            "accountsOnly",
            "firstEnableRequest",
            "riskSettings",
          ])
        ).finally(() => {
          setIsInitialLoad(false);
          setIsLoadingisLoadingList(false);
        });
      }
    };

    fetchRequestsPeriodically();

    const intervalId = setInterval(fetchRequestsPeriodically, 60000);

    return () => clearInterval(intervalId);
  }, [isModalOpen, dispatch, isInitialLoad]);

  useEffect(() => {
    if (TGAccountsList !== null) {
      let sortedAccounts = null;
      if (TGAccountsList.length) {
        sortedAccounts = {};
        TGAccountsList.forEach((item) => {
          const { software, softwareType } = item;
          if (software) {
            const combinedValue = `${software} ${softwareType}`;

            if (!sortedAccounts[combinedValue]) {
              sortedAccounts[combinedValue] = [];
            }

            // For simulator accounts, check if there's a live account with the same email
            if (softwareType === "Sim") {
              const hasLiveAccount = TGAccountsList.some((otherItem) => 
                otherItem.personal?.email === item.personal?.email && 
                otherItem.softwareType !== "Sim"
              );
              // Only add simulator account if no live account exists with same email and account is not suspended
              if (!hasLiveAccount && item.status !== "AccountSuspended") {
                sortedAccounts[combinedValue].push(item);
              }
            } else {
              // Add all non-simulator accounts
              sortedAccounts[combinedValue].push(item);
            }
          }
        });
      }

      setSortedTraders(sortedAccounts);
    }
  }, [TGAccountsList]);

  useEffect(() => {
    if (
      sortedTraders &&
      Object.keys(sortedTraders).length > 0 &&
      tradingPlatforms &&
      !activeTP
    ) {
      const newActiveTP = findActiveTP(
        tradingPlatforms,
        Object.keys(sortedTraders)[0]
      );
      setActiveTP(newActiveTP);
    }
  }, [sortedTraders, tradingPlatforms, activeTP]);

  // useEffect(() => {
  //   if (activeTP && sortedTraders) {
  //     const defaultVisibleItems =
  //       activeList.length > 10 ? 10 : activeList.length;
  //     setVisibleItems((prevVisibleItems) =>
  //       prevVisibleItems > defaultVisibleItems ? defaultVisibleItems : defaultVisibleItems
  //     );
  //   }
  // }, [activeTP, sortedTraders, activeList.length]);

  const checkboxColumnsHandler = (row) => {
    setCheckedItems((prev) => {
      const currentIndex = prev.find((p) => p.id === row.id);
      if (currentIndex === undefined) {
        // Not currently checked, add it
        return [...prev, row];
      } else {
        // Currently checked, remove it
        return prev.filter((r) => r.id !== row.id);
      }
    });
  };
  const starColumsHandler = (row, status) => {};

  const onTPButtonClick = (tpName) => {
    const fountTP = findActiveTP(tradingPlatforms, tpName);
    if (fountTP) {
      setActiveTP(fountTP);
      setCheckedItems([]);
    }
  };

  return (
    <div className={cn("page-wrapper", styles.traders)}>
      <div className={cn(styles.header, "d-flex f-gap-20 align-items-center")}>
        <h3 className={styles.title}>Traders</h3>
        {!isLoadingList && (
          <>
            <div className={styles.tpScroll}>
              {sortedTraders && (
                <div className={styles.tpBlock}>
                  {Object.keys(sortedTraders)?.map((i) => (
                    <span
                      onClick={() => onTPButtonClick(i)}
                      className={cn(styles.tpItem, {
                        [styles["active-trading-platform"]]:
                          i === `${activeTP?.software} ${activeTP?.softwareType}`,
                      })}
                      key={crypto.randomUUID()}
                    >
                      {i}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <NewRequest
              setIsModalOpen={setIsModalOpen}
              adminGroupData={adminGroupData}
              renderFullListAfterRequest={false}
              multipleTraders={checkedItems}
              setCheckedItems={setCheckedItems}
              filteredCategoriesArray={[categoryDataType.MARKETDATA]}
              disabled={activeTP?.softwareType === "Sim"}
            />
          </>
        )}
      </div>
      {!isLoadingList && activeList && (
        <div className={styles.body}>
          <div className={styles["table-body"]}>
            <TableComponent
              data={tableFormattedData(
                true,
                activeList,
                riskSettingMetadata
              )}
              dateColumns={["created", "updated"]}
              checkedItems={checkedItems}
              checkboxColumsHandler={checkboxColumnsHandler}
              starColumsHandler={starColumsHandler}
              formattingForDateColumns={supportTableUtils.tableTimeFormat}
              showSearch
              searchPlaceholder="Search by trader id, name, platform, account state, etc."
              globalFilterColumnIds={["traderId", "Full Name", "platform", "Account State", "Max Loss", "Buying Power", "Max Position Exposure", "Max Loss (Auto Liquidate)"]}
              globalFilterResetKey={`${activeTP?.software ?? ""} ${activeTP?.softwareType ?? ""}`}
              // onFilteredRowsChange={setFilteredMeta}
              // isLoading={isLoading}
              //   setCellClassName={setCellClassName}
              itemsToUseSorting={[
                "subgroup",
                "traderid",
                "accountstate",
                "platform",
                "fullname",
              ]}
            />
          </div>
          {/* Pagination / "Showing n of n" disabled — show all rows above */}
          {/* <SupportStateController
            setVisibleItems={setVisibleItems}
            visibleItems={visibleItems}
            items={activeList}
            visibleItemsOverride={
              filteredMeta.isFiltering ? filteredMeta.filteredCount : undefined
            }
            totalItemsOverride={
              filteredMeta.isFiltering ? filteredMeta.filteredCount : undefined
            }
            hideControls={filteredMeta.isFiltering}
          /> */}
        </div>
      )}
    </div>
  );
};

export default Traders;
